import { z } from "zod";
import { INVITABLE_ROLES, WORKSPACE_ROLES } from "./roles.js";

export const slugSchema = z
  .string()
  .min(3, "Use pelo menos 3 caracteres.")
  .max(40, "Use no máximo 40 caracteres.")
  .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, "Só letras minúsculas, números e hífens.");

export const workspaceNameSchema = z
  .string()
  .trim()
  .min(2, "O nome precisa ter pelo menos 2 caracteres.")
  .max(80, "O nome pode ter no máximo 80 caracteres.");

export const createWorkspaceSchema = z.object({
  name: workspaceNameSchema,
  slug: slugSchema.optional(),
});
export type CreateWorkspaceInput = z.infer<typeof createWorkspaceSchema>;

export const updateWorkspaceSchema = z.object({
  name: workspaceNameSchema.optional(),
  brandColor: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/, "Use uma cor no formato #RRGGBB.")
    .nullable()
    .optional(),
});
export type UpdateWorkspaceInput = z.infer<typeof updateWorkspaceSchema>;

export const createInviteSchema = z.object({
  email: z
    .email("Informe um e-mail válido.")
    .max(254)
    .transform((v) => v.trim().toLowerCase()),
  role: z.enum(INVITABLE_ROLES),
});
export type CreateInviteInput = z.infer<typeof createInviteSchema>;

export const updateMemberRoleSchema = z.object({
  role: z.enum(WORKSPACE_ROLES),
});
export type UpdateMemberRoleInput = z.infer<typeof updateMemberRoleSchema>;

export const signUpSchema = z.object({
  name: z.string().trim().min(2, "Informe seu nome.").max(80),
  email: z.email("Informe um e-mail válido."),
  password: z.string().min(8, "A senha precisa ter pelo menos 8 caracteres.").max(128),
});
export type SignUpInput = z.infer<typeof signUpSchema>;

export const signInSchema = z.object({
  email: z.email("Informe um e-mail válido."),
  password: z.string().min(1, "Informe a senha."),
});
export type SignInInput = z.infer<typeof signInSchema>;

// ---- Fase 3: gestão de BM, contas e clientes -----------------------------------------
import {
  META_ASSET_TYPES,
  META_BUSINESS_VERTICALS,
  META_CURRENCIES,
  META_INVITABLE_BUSINESS_ROLES,
  META_PARTNER_AD_ACCOUNT_TASKS,
  META_SYSTEM_USER_ROLES,
  META_TIMEZONES,
} from "./meta-enums.js";

const metaIdSchema = z
  .string()
  .trim()
  .regex(/^(act_)?\d{1,30}$/, "Informe um id numérico da Meta.");

export const createAdAccountSchema = z.object({
  name: z
    .string()
    .trim()
    .min(2, "O nome precisa ter pelo menos 2 caracteres.")
    .max(100, "O nome pode ter no máximo 100 caracteres."),
  currency: z.enum(META_CURRENCIES, { message: "Escolha uma moeda." }),
  timezoneId: z
    .number()
    .int()
    .refine((v) => META_TIMEZONES.some((t) => t.id === v), "Escolha um fuso horário."),
  /** Para quem os anúncios são veiculados: a página do negócio (id) ou nenhum (NONE). */
  endAdvertiser: z.union([z.literal("NONE"), metaIdSchema]).default("NONE"),
  /** Finalidade comercial informada à Meta: o próprio negócio ou conta de cliente da agência. */
  purpose: z.enum(["propria", "cliente"]).default("propria"),
});
export type CreateAdAccountInput = z.infer<typeof createAdAccountSchema>;

export const updateAdAccountSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(2, "O nome precisa ter pelo menos 2 caracteres.")
      .max(100)
      .optional(),
    /** Em centavos. 0 ou null remove o limite. */
    spendCapCents: z
      .number()
      .int("Use um valor inteiro em centavos.")
      .min(0, "O limite não pode ser negativo.")
      .max(1_000_000_000_000)
      .nullable()
      .optional(),
  })
  .refine((v) => v.name !== undefined || v.spendCapCents !== undefined, {
    message: "Informe o nome ou o limite de gasto.",
  });
export type UpdateAdAccountInput = z.infer<typeof updateAdAccountSchema>;

export const assignAssetUserSchema = z.object({
  /** Id da pessoa (business user) ou do system user na Meta. */
  userId: metaIdSchema,
  tasks: z.array(z.string().trim().min(1)).min(1, "Escolha pelo menos uma tarefa."),
});
export type AssignAssetUserInput = z.infer<typeof assignAssetUserSchema>;

export const metaAssetTypeSchema = z.enum(META_ASSET_TYPES);

export const inviteBusinessUserSchema = z.object({
  email: z
    .email("Informe um e-mail válido.")
    .max(254)
    .transform((v) => v.trim().toLowerCase()),
  role: z.enum(META_INVITABLE_BUSINESS_ROLES, { message: "Escolha um papel." }),
});
export type InviteBusinessUserInput = z.infer<typeof inviteBusinessUserSchema>;

export const updateBusinessUserSchema = z.object({
  role: z.enum(META_INVITABLE_BUSINESS_ROLES, { message: "Escolha um papel." }),
});
export type UpdateBusinessUserInput = z.infer<typeof updateBusinessUserSchema>;

export const addPartnerSchema = z.object({
  /** Id do Business Manager do parceiro. */
  partnerBusinessId: metaIdSchema,
  /** Conta de anúncio (act_...) que será compartilhada com o parceiro. */
  adAccountId: metaIdSchema,
  tasks: z.array(z.enum(META_PARTNER_AD_ACCOUNT_TASKS)).min(1, "Escolha pelo menos uma tarefa."),
});
export type AddPartnerInput = z.infer<typeof addPartnerSchema>;

export const createSystemUserSchema = z.object({
  name: z.string().trim().min(2, "Informe um nome.").max(80),
  role: z.enum(META_SYSTEM_USER_ROLES, { message: "Escolha um papel." }),
});
export type CreateSystemUserInput = z.infer<typeof createSystemUserSchema>;

export const createBusinessSchema = z.object({
  name: z.string().trim().min(2, "Informe o nome do negócio.").max(100),
  vertical: z.enum(META_BUSINESS_VERTICALS, { message: "Escolha o segmento." }),
  /** Página principal do negócio (id da Meta). */
  primaryPageId: metaIdSchema,
  timezoneId: z
    .number()
    .int()
    .refine((v) => META_TIMEZONES.some((t) => t.id === v), "Escolha um fuso horário."),
  /** Conexão (perfil Meta) em nome de quem o BM é criado. */
  connectionId: z.uuid("Escolha a conta Meta."),
});
export type CreateBusinessInput = z.infer<typeof createBusinessSchema>;

export const clientColorSchema = z
  .string()
  .regex(/^#[0-9a-fA-F]{6}$/, "Use uma cor no formato #RRGGBB.");

export const createClientSchema = z.object({
  name: z
    .string()
    .trim()
    .min(2, "O nome precisa ter pelo menos 2 caracteres.")
    .max(80, "O nome pode ter no máximo 80 caracteres."),
  color: clientColorSchema.default("#2563eb"),
  notes: z.string().trim().max(2000, "Use no máximo 2000 caracteres.").default(""),
});
export type CreateClientInput = z.infer<typeof createClientSchema>;

export const updateClientSchema = z.object({
  name: createClientSchema.shape.name.optional(),
  color: clientColorSchema.optional(),
  notes: z.string().trim().max(2000).optional(),
});
export type UpdateClientInput = z.infer<typeof updateClientSchema>;

/** Conjunto completo de ativos do cliente (substitui o vínculo atual). */
export const setClientAssetsSchema = z.object({
  businessMetaIds: z.array(metaIdSchema).max(500).default([]),
  adAccountMetaIds: z.array(metaIdSchema).max(500).default([]),
  pageMetaIds: z.array(metaIdSchema).max(500).default([]),
});
export type SetClientAssetsInput = z.infer<typeof setClientAssetsSchema>;

/** Membros vinculados ao cliente (substitui o vínculo atual). */
export const setClientMembersSchema = z.object({
  memberIds: z.array(z.uuid()).max(500).default([]),
});
export type SetClientMembersInput = z.infer<typeof setClientMembersSchema>;

// ---- Fase 4: campanhas, conjuntos e anúncios -----------------------------------------------
import {
  BUDGET_PERCENT_MAX,
  BUDGET_PERCENT_MIN,
  CAMPAIGN_LEVELS,
  CAMPAIGN_OBJECTIVES,
  COPY_STATUS_OPTIONS,
  EFFECTIVE_STATUSES,
  PERIOD_PRESETS,
} from "./campaign-enums.js";

export const campaignLevelSchema = z.enum(CAMPAIGN_LEVELS);

const entityNameSchema = z
  .string()
  .trim()
  .min(1, "O nome não pode ficar vazio.")
  .max(400, "O nome pode ter no máximo 400 caracteres.");

/** Mudança de orçamento: valor final em centavos ou variação em %. */
export const budgetChangeSchema = z.discriminatedUnion("mode", [
  z.object({
    mode: z.literal("absoluto"),
    value: z
      .number()
      .int("Use um valor inteiro em centavos.")
      .positive("O orçamento precisa ser maior que zero.")
      .max(1_000_000_000_000),
  }),
  z.object({
    mode: z.literal("percentual"),
    value: z
      .number()
      .min(BUDGET_PERCENT_MIN, `A variação mínima é ${BUDGET_PERCENT_MIN}%.`)
      .max(BUDGET_PERCENT_MAX, `A variação máxima é ${BUDGET_PERCENT_MAX}%.`)
      .refine((v) => v !== 0, "Informe uma variação diferente de zero."),
  }),
]);
export type BudgetChangeInput = z.infer<typeof budgetChangeSchema>;

/** Edição inline: nome, status (ACTIVE/PAUSED) e/ou orçamento. */
export const updateCampaignEntitySchema = z
  .object({
    name: entityNameSchema.optional(),
    status: z.enum(["ACTIVE", "PAUSED"]).optional(),
    budget: budgetChangeSchema.optional(),
  })
  .refine((v) => v.name !== undefined || v.status !== undefined || v.budget !== undefined, {
    message: "Informe o nome, o status ou o orçamento.",
  });
export type UpdateCampaignEntityInput = z.infer<typeof updateCampaignEntitySchema>;

export const duplicateEntitySchema = z.object({
  /** PAUSED (padrão da Meta), ACTIVE ou INHERITED_FROM_SOURCE. */
  statusOption: z.enum(COPY_STATUS_OPTIONS).default("PAUSED"),
  /** Copia também os filhos (conjuntos e anúncios). */
  deepCopy: z.boolean().default(false),
  /** Sufixo do nome (com o espaço que quiser); vazio = "- Cópia" da própria Meta. */
  renameSuffix: z.string().max(100).optional(),
  /** Outra conta do workspace (act_...). A Meta não aceita no `/copies`; a interface explica. */
  targetAdAccountMetaId: z
    .string()
    .trim()
    .regex(/^act_\d{1,30}$/, "Informe uma conta de anúncio válida.")
    .optional(),
});
export type DuplicateEntityInput = z.infer<typeof duplicateEntitySchema>;

export const BULK_ACTIONS = ["ativar", "pausar", "excluir", "orcamento", "duplicar"] as const;
export type BulkAction = (typeof BULK_ACTIONS)[number];

const metaNumericIdSchema = z
  .string()
  .trim()
  .regex(/^\d{1,30}$/, "Id inválido.");

export const bulkActionSchema = z
  .object({
    level: campaignLevelSchema,
    adAccountMetaId: z
      .string()
      .trim()
      .regex(/^act_\d{1,30}$/, "Conta inválida."),
    ids: z.array(metaNumericIdSchema).min(1, "Selecione pelo menos um item.").max(200),
    action: z.enum(BULK_ACTIONS),
    budget: budgetChangeSchema.optional(),
    duplicate: duplicateEntitySchema.optional(),
  })
  .refine((v) => v.action !== "orcamento" || v.budget !== undefined, {
    message: "Informe a mudança de orçamento.",
    path: ["budget"],
  });
export type BulkActionInput = z.infer<typeof bulkActionSchema>;

/** Filtros da listagem (query string). */
export const campaignListQuerySchema = z.object({
  campaignId: metaNumericIdSchema.optional(),
  adsetId: metaNumericIdSchema.optional(),
  q: z.string().trim().max(200).optional(),
  status: z.enum(["ACTIVE", "PAUSED", "ARCHIVED"]).optional(),
  effectiveStatus: z.enum(EFFECTIVE_STATUSES).optional(),
  objective: z.enum(CAMPAIGN_OBJECTIVES).optional(),
  /** Período das métricas (Fase 6). Com `dateField`, também filtra por data de criação/atualização. */
  preset: z.enum(PERIOD_PRESETS).optional(),
  from: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  to: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  dateField: z.enum(["created", "updated"]).optional(),
  includeDeleted: z.coerce.boolean().default(false),
});
export type CampaignListQuery = z.infer<typeof campaignListQuerySchema>;

/** Preferência de colunas por usuário e tabela (`campaigns:campaigns`, etc.). */
export const tablePrefsSchema = z.object({
  columns: z.array(z.string().trim().min(1).max(40)).max(50),
});
export type TablePrefsInput = z.infer<typeof tablePrefsSchema>;
